import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import RideCard from "@/components/RideCard";
import { api, formatApiError } from "@/lib/api";
import { Calendar, Plus } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

function groupByDay(rides) {
  const groups = new Map();
  for (const r of rides) {
    const d = new Date(r.start_datetime);
    const key = format(d, "yyyy-MM-dd");
    if (!groups.has(key)) groups.set(key, { date: d, items: [] });
    groups.get(key).items.push(r);
  }
  return Array.from(groups.values());
}

export default function Planned() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rides/user/me", { params: { upcoming: true } });
        setRides(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Errore caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = groupByDay(rides);

  return (
    <>
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <header className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="label-tag mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Calendario personale</div>
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900">
              In <span className="text-[#E11D48]">programma</span>
            </h1>
            <p className="text-gray-500 text-sm mt-2 max-w-xl">
              I giri che hai pubblicato o ai quali ti sei iscritto, in ordine cronologico.
            </p>
          </div>
          <Link to="/publish" className="btn-primary px-5 py-3 rounded-sm text-xs flex items-center gap-2" data-testid="planned-publish-cta">
            <Plus className="w-4 h-4" /> Pubblica un giro
          </Link>
        </header>

        {loading && (
          <div className="tactical-card rounded-sm p-12 text-center text-gray-500 text-sm">Caricamento...</div>
        )}

        {!loading && error && (
          <div className="tactical-card rounded-sm p-12 text-center">
            <div className="text-gray-700 font-bold mb-2">{error}</div>
          </div>
        )}

        {!loading && !error && rides.length === 0 && (
          <div className="tactical-card rounded-sm p-12 text-center" data-testid="planned-empty">
            <Calendar className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <div className="font-display font-bold text-xl text-gray-900 mb-2">Nessun giro in programma</div>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
              Iscriviti a un giro pubblicato da altri ciclisti nell'<Link to="/feed" className="text-[#E11D48] hover:underline">esplora</Link>, oppure pubblica il tuo prossimo giro.
            </p>
            <Link to="/publish" className="btn-primary px-5 py-3 rounded-sm text-xs inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Pubblica giro
            </Link>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <div className="space-y-8" data-testid="planned-list">
            {groups.map((g) => (
              <section key={format(g.date, "yyyy-MM-dd")}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <div className="label-tag font-mono text-[#E11D48]">
                    {format(g.date, "EEEE d MMMM", { locale: it })}
                  </div>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {g.items.map((r) => (
                    <RideCard key={r.ride_id} ride={r} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
